import {Injectable, inject, NgZone} from '@angular/core';
import { UrlUtils } from '../shared/utils/url.utils';
import { State } from '../shared/configurations/state.constant';
import { JwtUtilService } from './jwt-util.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription, timer} from 'rxjs';
import {RestApiBase} from "../shared/configurations/rest-api-base.config";

@Injectable({ providedIn: 'root' })
export class AuthService {

    private readonly MIN_TO_MS = 60000;
    private readonly EXPIRE_BUFFER_PERCENT = 0.95;
    public PROFILE_PAGE = '/esploro/profile';

    private _redirectUrl;
    private _redirectRoute;
    private jwtUtilService = inject(JwtUtilService);
    private http = inject(HttpClient);
    private router = inject(Router);
    private ngZone = inject(NgZone);
    private refreshSub: Subscription;

    constructor(){
        this.redirectUrl = this.getProfilePage();
        this.redirectRoute = State.PROFILE;
    }

    get redirectUrl() {
        return this._redirectUrl;
    }

    set redirectUrl(value) {
        this._redirectUrl = value;
    }

    get redirectRoute() {
        return this._redirectRoute;
    }

    set redirectRoute(value) {
        this._redirectRoute = value;
    }

    getProfilePage() {
        return window.location.origin + this.PROFILE_PAGE+ '?institution=' + UrlUtils.getParam('institution');
    }

    getLoginPage() {
        return window.location.origin + '/esploro/login?institution=' + UrlUtils.getParam('institution');
    }

    getLoginAsPage() {
        return window.location.origin + '/esploro/login-as?institution=' + UrlUtils.getParam('institution');
    }

    getUrlByRoute(route: string) {
        return route ? window.location.origin + '/esploro' + route : this.redirectUrl;
    }

    handleResearcherLogin(jwt) {
        this.jwtUtilService.storeJwt(jwt);
        this.startTokenMonitor();
    }

    handleAdminLogin(jwt: string) {
        this.jwtUtilService.storeJwtInSessionStorage(jwt);
        this.startTokenMonitor();
    }

    startTokenMonitor() {
        this.stopTokenMonitor();
        if (!this.jwtUtilService.isLoggedIn()) {
            this.logMonitorRefresh('No JWT found, not starting token monitor');
            return;
        }
        this.logMonitorRefresh('Starting token monitor at: ' + new Date().toLocaleTimeString());
        this.tokenMonitor();
    }

    stopTokenMonitor() {
        this.logMonitorRefresh('Check if token monitor is running');
        if (this.refreshSub) {
            this.logMonitorRefresh('Stopping token monitor');
            this.refreshSub.unsubscribe();
        }
    }

    private tokenMonitor() {
        const expireInMin = this.jwtUtilService.getMinutesLeft();
        const normalizeRefreshInterval = expireInMin * this.MIN_TO_MS * this.EXPIRE_BUFFER_PERCENT;
        this.logMonitorRefresh(`Token expires in ${expireInMin} minutes`);
        this.logMonitorRefresh(`Going to refresh in ${(normalizeRefreshInterval) / this.MIN_TO_MS} minutes`);
        this.refreshSub = timer(normalizeRefreshInterval).subscribe(() => {
            this.logMonitorRefresh('Checking token expiration');
            const minutesLeft = this.jwtUtilService.getMinutesLeft();
            this.logMonitorRefresh(`Minutes left for token expiration: ${minutesLeft}`);
            if (minutesLeft <= 0) {
                this.logMonitorRefresh('Token expired, logging out');
                this.logout();
            } else {
                this.logMonitorRefresh(`Token expiring in ${this.jwtUtilService.getMinutesLeft()} minutes, refreshing`);
                this.silentRefresh();
            }
        });
    }

    private silentRefresh() {
        const jwt = this.jwtUtilService.getJwtFromStorage();
        if (!jwt) return;
        this.http.get(RestApiBase.API_ENDPOINT + '/auth/refresh')
            .subscribe({
                next: (newJwt: string) => {
                    if (this.jwtUtilService.isAdminLoggedIn()) {
                        this.handleAdminLogin(newJwt);
                    } else {
                        this.handleResearcherLogin(newJwt);
                    }
                },
                error: () => {
                    this.logout();
                }
            });
    }

    logout() {
        this.jwtUtilService.removeJwt();
        this.jwtUtilService.removeJwtFromSessionStorage();
        this.stopTokenMonitor();
        this.router.navigate(['/login'], { queryParams: { institution: UrlUtils.getParam('institution') } });
    }

    private logMonitorRefresh(message: string) {
        // if (isNotProduction()) {
        //     console.debug(message);
        // }
    }
}
